import React from 'react'
import Head from 'next/head'
import { Restaurant, LocalBusiness, WithContext } from 'schema-dts'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { getDefaultCentreMetadata } from '@scentregroup/shared/helpers/get-default-centre-metadata'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { DateTime } from '@scentregroup/shared/helpers/date-helpers'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { getRedirectForMismatchedRoute } from '@scentregroup/shared/helpers/get-redirect-for-mismatched-route'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../../types/next'
import { makeApolloClient } from '../../../../lib/apolloClient'
import { getCentreStorefront } from '../../../../lib/page-templates/fetching'
import { SGPageTemplateWithAdZones } from '../../../../../../src/types/page-templates'
import { OpenGraph } from '../../../../lib/open-graph'
import {
  ContentDetail,
  getContentDetail,
} from '../../../../lib/get-content-detail'
import { StoreContentDetail } from '../../../../components/page-templates/content-detail/store'
import {
  CentreSlug,
  DateString,
  getTradingHoursForCentre,
  getTradingHoursForStore,
  StoreId,
} from '../../../../lib/get-trading-hours'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
    id: string
    slug: string
  }>
): Promise<GetServerSidePropsResult<ICentreStorefrontPageProps>> {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const centreSlug = ctx.params?.centre
  const storeId = ctx.params?.id
  if (!centreSlug || !storeId) {
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()
  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }
  const redirect = getRedirectForMismatchedRoute(
    '/[centre]/store/[id]/[slug]',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirect) {
    return { redirect }
  }
  if (storeId === centre?.cinema?.sys.id) {
    return {
      redirect: {
        permanent: false,
        destination: `/${centre.slug}/movies`,
      },
    }
  }
  const [template, store, headerStaticData, topStripProps] = await Promise.all([
    getCentreStorefront(apollo, centreSlug, storeId),
    getContentDetail(apollo, 'store', { id: storeId }),
    fetchCentreStaticData(apollo, {
      country: localeToCountry(ctx.locale),
      centre,
    }),
    getTopStripProps(apollo, country, centreSlug),
  ])
  if (!store || !template) {
    return {
      notFound: true,
    }
  }
  const storeRedirect = getRedirectForMismatchedRoute(
    '/[centre]/store/[id]/[slug]',
    ctx.params,
    {
      slug: store.slug,
    }
  )
  if (storeRedirect) {
    return { redirect: storeRedirect }
  }
  template.modules.forEach(module => {
    if (module.__typename === 'SGPageTemplateBasicContent') {
      module.titleAs = 'h2'
    }
  })
  const startDate = DateTime.moment()
    .tz(centre.timeZone)
    .startOf('day')
    .format('YYYY-MM-DD')
    .toString() as DateString
  const [storeTradingHours, centreTradingHours] = await Promise.all([
    getTradingHoursForStore(
      apollo,
      centreSlug as CentreSlug,
      storeId as StoreId,
      startDate
    ),
    getTradingHoursForCentre(apollo, centreSlug as CentreSlug, startDate),
  ])
  return {
    props: {
      country,
      topStripProps,
      centre,
      store,
      template,
      headerStaticData,
      storeTradingHours,
      centreTradingHours,
    },
  }
}

export interface ICentreStorefrontPageProps {
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  centre: CentreDetails
  store: ContentDetail['store']
  template: SGPageTemplateWithAdZones
  headerStaticData: CentreStaticData
  storeTradingHours: TradingHourDay[]
  centreTradingHours: TradingHourDay[]
}

export default function CentreStorefrontPage({
  country,
  topStripProps,
  centre,
  store,
  template,
  headerStaticData,
  storeTradingHours,
  centreTradingHours,
}: ICentreStorefrontPageProps): JSX.Element {
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const meta = getDefaultCentreMetadata({
    centre,
    canonical,
  })

  const metaData = {
    title: `${store.title} at Westfield ${centre.title}`,
    description: `Westfield ${centre.title} ${store.title} - Check opening hours, phone number, address & directions. Find 100s of quality retailers like ${store.title} & more at Westfield ${centre.title}!`,
  }

  const storeTypeIsRestaurant = store.retailCategoriesCollection?.items.find(
    s => s?.title === 'Food and drink'
  )
  const ldLocalBusiness = useLDLocalBusiness(store, centre, canonical)
  const ldRestaurant = useLDRestaurant(
    store,
    centre,
    canonical,
    storeTradingHours
  )

  const ldSchema = storeTypeIsRestaurant ? ldRestaurant : ldLocalBusiness

  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  return (
    <>
      <SGLinkedData item={meta.schema} />
      <SGLinkedData item={ldSchema} />
      <OpenGraph data={meta.openGraphData} />
      <Head>
        <title>{metaData.title}</title>
        <meta
          key="description"
          name="description"
          content={metaData.description}
        />
        <link key="canonical" rel="canonical" href={canonical} />
      </Head>
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={{
          ...centre,
          contactLinks,
        }}
        header={{
          variant: 'centre',
          data: headerStaticData,
          hasBottomMargin: [false, false, true],
          logoText: centre.title,
        }}
        metaData={metaData}
      >
        <StoreContentDetail
          centre={centre}
          content={store}
          template={template}
          storeTradingHours={storeTradingHours}
          centreTradingHours={centreTradingHours}
        />
      </Layout>
    </>
  )
}

function useLDRestaurant(
  store: ContentDetail['store'],
  centre: CentreDetails,
  canonical: string,
  storeTradingHours?: TradingHourDay[]
): WithContext<Restaurant> {
  return React.useMemo<WithContext<Restaurant>>(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      '@id': `${canonical}#restaurant`,
      name: store.title,
      description: store.shortDescription ?? undefined,
      image: [
        store.storeFrontImage,
        store.retailer.retailerLogoSet?.coloured?.url,
        store.retailer.heroImageSet?.master?.url,
      ].filter((elem): elem is string => Boolean(elem)),
      telephone: store.telephoneNumber ?? undefined,
      url: canonical,
      sameAs: store.retailer.websiteAddress ?? undefined,
      openingHoursSpecification:
        storeTradingHours &&
        storeTradingHours.map(tradingHoursOfADay => ({
          '@type': 'OpeningHoursSpecification',
          opens: tradingHoursOfADay.openingTime,
          closes: tradingHoursOfADay.closingTime,
          validFrom: tradingHoursOfADay.date,
          validThrough: tradingHoursOfADay.date,
        })),
      address: {
        '@type': 'PostalAddress',
        addressLocality: centre.suburb,
        addressRegion: centre.state,
        postalCode: centre.postcode,
        streetAddress: centre.physicalAddress,
      },
    }),
    [
      store.title,
      store.shortDescription,
      store.storeFrontImage,
      store.retailer,
      store.telephoneNumber,
      canonical,
      centre.suburb,
      centre.state,
      centre.postcode,
      centre.physicalAddress,
      storeTradingHours,
    ]
  )
}

function useLDLocalBusiness(
  store: ContentDetail['store'],
  centre: CentreDetails,
  canonical: string
): WithContext<LocalBusiness> {
  return React.useMemo<WithContext<LocalBusiness>>(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${canonical}#localbusiness`,
      name: store.title,
      description: store.shortDescription ?? undefined,
      image: [
        store.storeFrontImage,
        store.retailer.retailerLogoSet?.coloured?.url,
        store.retailer.heroImageSet?.master?.url,
      ].filter((elem): elem is string => Boolean(elem)),
      telephone: store.telephoneNumber ?? undefined,
      url: canonical,
      sameAs: store.retailer.websiteAddress ?? undefined,
      address: {
        '@type': 'PostalAddress',
        addressLocality: centre.suburb,
        addressRegion: centre.state,
        postalCode: centre.postcode,
        streetAddress: centre.physicalAddress,
      },
    }),
    [
      store.title,
      store.shortDescription,
      store.storeFrontImage,
      store.retailer,
      store.telephoneNumber,
      canonical,
      centre.suburb,
      centre.state,
      centre.postcode,
      centre.physicalAddress,
    ]
  )
}
